import axios, { AxiosError } from "axios";
import { appContext } from "../AppContext";
import { RouteHelper } from '../Common/RouteHelper'
import { IAxiosRequestConfig } from "@iconics/core";
import ApiMessageBuilder from "./ApiMessageBuilder";
import Authentication, { LogOutReason } from "../Common/Authentication";

export class ApiClient implements IApiClient
{
    private readonly authentication: Authentication;
    private readonly routeHelper = new RouteHelper();
    private messageBuilder = new ApiMessageBuilder();

    constructor(authentication: Authentication)
    {
        this.authentication = authentication;
    }

    public initialise(): void
    {
        const productName = "IBSS Hub";
        const version = process.env.REACT_APP_VERSION;

        axios.interceptors.request.use(request =>
        {
            request.headers["App-Version"] = `${productName}/${version}`;

            const authToken = this.authentication.getToken();
            if (authToken)
            {
                request.headers["Authorization"] = `bearer ${authToken}`;
            }

            const currentLang = appContext().language.current;
            if (currentLang != null && currentLang !== "")
            {
                request.headers["Accept-Language"] = currentLang;
            }

            return request;
        });

        axios.interceptors.response.use(
            response =>
            {
                return response;
            },
            (error: AxiosError) =>
            {
                const config = error.config as IAxiosRequestConfig | undefined;
                const statusCode = error?.response?.status;
                const route = this.routeHelper.getCurrentRoute();
                const isPublic = (route?.isPublic ?? false);
                const isLoggingIn = (route?.path == "/auth" || route?.path == "/login/success");
                const suppressErrorPopup = (config?.suppressErrorPopup ?? false);

                if (statusCode === 401 && !isPublic)
                {
                    // log out if we get another 401 after refreshing the access token (to guard against an infinite loop)
                    if (!config || config.tokenRefreshed == true)
                    {
                        this.authentication.logOut(LogOutReason.SessionExpired);
                        return Promise.reject(error);
                    }

                    return this.authentication
                        .refreshToken()
                        .then(token =>
                        {
                            config.tokenRefreshed = true;
                            config.headers["Authorization"] = `bearer ${token}`;
                            return axios(config);
                        })
                        .catch(() =>
                        {
                            this.authentication.logOut(LogOutReason.SessionExpired);
                            return Promise.reject(error);
                        });
                }
                else if (statusCode === 403 && !isPublic)
                {
                    if (!suppressErrorPopup)
                    {
                        const apiError = this.messageBuilder.getApiError(error);

                        appContext().state.set({
                            showApiError: true,
                            apiErrorTitle: apiError.errorTitle,
                            apiErrorDetails: apiError.errorDetails,
                            apiErrorMessages: apiError.messages,
                            apiErrorStatus: apiError.status,
                            apiErrorInstance: apiError.instance,
                            apiErrorTime: apiError.time,
                        });
                    }
                    return Promise.reject(error);
                }
                else if (statusCode === 403 && isLoggingIn)
                {
                    this.authentication.logOut(LogOutReason.AccountIncomplete);
                    return Promise.reject(error);
                }
                else
                {
                    if (!suppressErrorPopup)
                    {
                        const apiError = this.messageBuilder.getApiError(error);

                        appContext().state.set({
                            showApiError: true,
                            apiErrorTitle: apiError.errorTitle,
                            apiErrorDetails: apiError.errorDetails,
                            apiErrorMessages: apiError.messages,
                            apiErrorStatus: apiError.status,
                            apiErrorInstance: apiError.instance,
                            apiErrorTime: apiError.time,
                        });
                    }
                    return Promise.reject(error);
                }
            });
    }
}

export interface IApiClient
{
    initialise(): void;
}
